import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	ArrowRightIcon,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Input,
	Label,
	UnderlineLink,
	tokens,
} from '@treams/treams-ui';

import Logo from '../../../../../../../src/packages/kakapo-ui-kit/components/common/Logo';
import PublicForm, { Background, Element } from '../../../components/authentication/PublicForm';

const TextLink = styled(UnderlineLink)`
	flex-shrink: 0;
	white-space: nowrap;
`;

type ErrorWithKey = GraphQLError & {
	extensions: {
		error?: {
			key?: string;
		};
	};
};

export default function PasswordChallenge(props: {
	login: string;
	context: string;
	error?: GraphQLError;
	challenge: graphql.PasswordChallenge;
	onCancel: () => void;
	onResponse: (response: graphql.UpdateSessionResponse) => void;
	isPrefill?: boolean;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const { login, context, challenge } = props;

	const [commit] = graphql.usePasswordChallengeMutation();
	const [commitResend] = graphql.useResendTemporaryPasswordMutation();

	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const resend = async () => {
		try {
			await commitResend({
				variables: {
					input: {
						login: props.login,
					},
				},
			});

			history.push('/check-inbox');
		} catch (errors) {
			setError(errors.graphQLErrors[0]);
		}
	};

	if (
		error &&
		(error as ErrorWithKey)?.extensions?.error?.key === 'errors.sessions.temporaryPasswordRequired'
	) {
		return (
			<Background>
				<Element>
					<Box textAlign="center">
						<Logo to="/" />
					</Box>
					<GraphQLErrorMessage error={error || props.error} />
					<Button
						title={t('common.next')}
						rightIcon={<ArrowRightIcon />}
						onClick={() => resend()}
					/>
				</Element>
			</Background>
		);
	}

	return (
		<Formik
			initialValues={{
				login,
				password: '',
			}}
			onSubmit={async ({ password }) => {
				try {
					const { data } = await commit({
						variables: {
							input: {
								response: {
									context,
									password: {
										username: challenge.username,
										password,
									},
								},
							},
						},
					});

					props.onResponse(data!.updateSession);
				} catch (errors) {
					setError(errors.graphQLErrors[0]);
				}
			}}
		>
			<PublicForm>
				<GraphQLErrorMessage error={error || props.error} />
				{props.isPrefill ? null : (
					<Box mb={3}>
						<Label mb={2}>{t('common.userNameOrEmail')}:</Label>
						<FlexGap alignItems="center" gap={tokens.gap.list}>
							<Box flexGrow={1} flexShrink={1} minWidth={0}>
								<Input.Formik name="login" autoFocus disabled />
							</Box>
							<TextLink as="button" type="button" color="secondary" small onClick={props.onCancel}>
								{t('auth.login.password.changeUsername')}
							</TextLink>
						</FlexGap>
					</Box>
				)}
				<Box mb={3}>
					<Label mb={2}>{t('common.password')}:</Label>
					<Input.Formik type="password" name="password" autoFocus />
				</Box>
				<FlexGap alignItems="center" gap={tokens.gap.list}>
					<Button.Formik
						title={t('common.next')}
						rightIcon={<ArrowRightIcon />}
						disableSuccess={Boolean(error)}
					/>
					<TextLink as={Link} to="/forgot-password" color="secondary" small>
						{t('common.forgottenPassword')}
					</TextLink>
				</FlexGap>
			</PublicForm>
		</Formik>
	);
}
